import { useTeam } from "$/contexts/team.ctx.tsx";
import { Card, Group, Stack, Text, Button, Divider, TextInput, ActionIcon, FileButton, Avatar } from "@mantine/core";
import { IconArrowRight, IconLogout2, IconDeviceFloppy, IconCameraUp, IconTrash, IconCopy } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useMap, useClipboard } from '@mantine/hooks';
import { useAuth } from "@/contexts/auth.ctx.tsx";
import { notifications } from '@mantine/notifications';
import type { Team } from '@prisma/client';
type TeamPartial = Partial<Team>;
import { useConfirmModal } from '@/componets/confirm.modal.tsx';
import TestLink from '@/componets/testlink.tsx';

interface TeamPanelProps {
    panelChangeReq: (_: string) => void;
}

export default function TeamPanel({ panelChangeReq }: TeamPanelProps) {
    const clipboard = useClipboard({ timeout: 800 });
    const { team, teamSize, joinTeam, createTeam, updateTeam, deleteTeam, leaveTeam, refreshTeam } = useTeam();
    const { accessor, injectHeader } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    const teamFormMap = useMap([  // The user modifiable fields for the team
        ['name', ''],
        ['website', ''],
        ['email', ''],
        ['phone', ''],
        ['areaServed', ''],
        ['officeLocation', ''],
        ['flavorText', ''],
    ]);
    const [srcImg, setSrcImg] = useState<string>('');
    const [newUpload, setNewUpload] = useState<File | null>(null);
    const { ConfirmModalComponent, openConfirmModal } = useConfirmModal();

    const deleteTeamMessage = "Are you sure you want to delete this team? This action cannot be undone, if other members are still in the team they will have the option to claim ownership.";
    const leaveTeamMessage = "Are you sure you want to leave this team? You will no longer be able to access the team's results or test link. You can join again later with the join code.";

    useEffect(() => {
        const fetchImage = async (testAccessID: string) => {
            console.log('fetchImage', accessor);
            const req = new Request(`/api/${testAccessID}/img`, {
                method: 'GET'
            });
            const res = await fetch(req);
            if (res.status === 200) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                setSrcImg(url);
            }
        }
        if (!!team) {
            teamFormMap.set('name', team.name || '');
            teamFormMap.set('website', team.website || '');
            teamFormMap.set('email', team.email || '');
            teamFormMap.set('phone', team.phone || '');
            teamFormMap.set('areaServed', team.areaServed || '');
            teamFormMap.set('officeLocation', team.officeLocation || '');
            teamFormMap.set('flavorText', team.flavorText || '');
            fetchImage(team.testAccessId)
                .catch(console.error)
                .finally(() => console.log('fetchImage::done'));
        }
    }, [team]);

    useEffect(() => {
        if (!!accessor) {
            setIsLoading(true);
            refreshTeam().then(() => {
                console.log('team refreshed', team);
            }).catch(() => {
                notifications.show({
                    title: 'Error',
                    message: 'Failed to refresh team',
                    color: 'red',
                });
            }).finally(() => setIsLoading(false));
        }
    }, [accessor]);

    useEffect(() => {
        if (!!newUpload) {
            setSrcImg(URL.createObjectURL(newUpload));
            uploadImage()
                .then(() => {
                    notifications.show({
                        title: 'Image Uploaded',
                        message: 'Your image has been uploaded',
                        color: 'yenta-light-blue'
                    });
                })
                .catch((err) => {
                    console.error('uploadImage::error', err);
                    notifications.show({
                        title: 'Image Upload Failed',
                        message: 'Your image has not been uploaded',
                        color: 'red'
                    });
                });
        }
    }, [newUpload]);

    const uploadImage = async () => {
        if (!newUpload || !team) return;
        const formData = new FormData();
        formData.append('file', newUpload);
        const req = new Request(`/api/auth/${team.testAccessId}/img`, {
            method: 'POST',
            body: formData
        });
        injectHeader(req);
        const res = await fetch(req);
        // const data = await res.text();
        if (res.status === 200) {
            console.log('uploadImage::success');
            return;
        }
    }

    const deleteImage = async () => {
        if (!team) return;
        const req = new Request(`/api/auth/${team.testAccessId}/img`, {
            method: 'DELETE'
        });
        injectHeader(req);
        await fetch(req);
        setSrcImg('');
    }

    const noTeamView = () => {
        return <Stack justify="center" align="center" gap={'md'}>
            <Text size={'lg'} fw={500} component="p">You are not in a team</Text>
            <Divider my={'sm'} w={'90%'} />
            <Text component="p" w={'82%'} ta={'center'}>
                Teams are a way to group users together, a team can share results and provide
                a mutual test link. Commonly used to put on team websites and pages. You can
                create a team or join one.
            </Text>
            <TextInput
                label="Join Code"
                size={'lg'}
                placeholder="Enter join code"
                mt={'md'}
                value={joinCode}
                disabled={isLoading}
                onChange={e => setJoinCode(e.target.value)}
                rightSection={<ActionIcon
                    size={'xl'}
                    loading={isLoading}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsLoading(true);
                        joinTeam(joinCode)
                            .then(() => {
                                notifications.show({
                                    title: 'Success',
                                    message: 'Joined team successfully',
                                    color: 'green',
                                });
                            })
                            .catch(() => {
                                notifications.show({
                                    title: 'Error',
                                    message: 'Failed to join team',
                                    color: 'red',
                                });
                            })
                            .finally(() => setIsLoading(false));
                    }}
                >
                    <IconArrowRight />
                </ActionIcon>}
            />
            <Divider label="Or" labelPosition="center" my={'sm'} w={'90%'} />
        </Stack>
    }

    const saveEnabled = () => {
        if (!team && !!teamFormMap.get('name')) return true;
        if (!!team) {
            switch (true) {
                case (!!teamFormMap.get('name') && team.name !== teamFormMap.get('name')):
                case (!!teamFormMap.get('website') && team.website !== teamFormMap.get('website')):
                case (!!teamFormMap.get('email') && team.email !== teamFormMap.get('email')):
                case (!!teamFormMap.get('phone') && team.phone !== teamFormMap.get('phone')):
                case (!!teamFormMap.get('areaServed') && team.areaServed !== teamFormMap.get('areaServed')):
                case (!!teamFormMap.get('officeLocation') && team.officeLocation !== teamFormMap.get('officeLocation')):
                case (!!teamFormMap.get('flavorText') && team.flavorText !== teamFormMap.get('flavorText')):
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }

    const teamForm = () => {
        return <Group grow={!!team}>
            <Stack gap={'md'} justify="center" align="stretch" w={'60%'} mx={'auto'}>
                <TextInput label="Name" size={'lg'}
                    value={teamFormMap.get('name')}
                    onChange={e => teamFormMap.set('name', e.target.value)}
                    required placeholder="Enter team name"
                />
                <TextInput label="Website" size={'lg'}
                    value={teamFormMap.get('website')}
                    onChange={e => teamFormMap.set('website', e.target.value)}
                    placeholder="https://ourteam.com"
                />
                <TextInput label="Email" size={'lg'}
                    value={teamFormMap.get('email')}
                    onChange={e => teamFormMap.set('email', e.target.value)}
                    placeholder="team@ourteam.com"
                />
                <TextInput label="Phone" size={'lg'}
                    value={teamFormMap.get('phone')}
                    onChange={e => teamFormMap.set('phone', e.target.value)}
                    placeholder="123-456-7890"
                />
                <TextInput label="Area Served" size={'lg'}
                    value={teamFormMap.get('areaServed')}
                    onChange={e => teamFormMap.set('areaServed', e.target.value)}
                    placeholder="Central & Western New York"
                />
                <TextInput label="Office Location" size={'lg'}
                    value={teamFormMap.get('officeLocation')}
                    onChange={e => teamFormMap.set('officeLocation', e.target.value)}
                    placeholder="Syracuse, NY"
                />
                <TextInput label="Flavor Text" size={'lg'}
                    value={teamFormMap.get('flavorText')}
                    onChange={e => teamFormMap.set('flavorText', e.target.value)}
                    placeholder="Voted #1 in service since 2015"
                />
            </Stack>
            {!!team && <Stack justify='center' align='center' w={'35%'}>
                <Avatar src={srcImg} size={'100px'} />
                <Group wrap='nowrap'>
                    <FileButton
                        accept='image/*'
                        onChange={setNewUpload}
                    >
                        {(props) => <Button {...props} leftSection={<IconCameraUp />}>Upload image</Button>}
                    </FileButton>
                    <Button variant='light' disabled={!srcImg} leftSection={<IconTrash />} onClick={deleteImage}>Delete</Button>
                </Group>
            </Stack>}
        </Group>

    }

    const submitForm = async () => {
        setIsLoading(true);
        if (!accessor) {
            notifications.show({
                title: 'Error',
                message: 'You must be logged in to save a team',
                color: 'red',
            });
            setIsLoading(false);
            return;
        }
        const data: TeamPartial = Object.fromEntries(Array.from(teamFormMap.entries()).filter(fd => {
            return !!fd[1];
        })) as TeamPartial;
        console.log(data);
        try {
            if (!!team) {
                data.id = team.id;
                await updateTeam(data)
            } else {
                await createTeam(data);
            }
            notifications.show({
                title: 'Success',
                message: `Team ${!!team ? 'updated' : 'created'} successfully`,
                color: 'green',
            });
        } catch (error) {
            console.error(error);
            notifications.show({
                title: 'Error',
                message: 'Failed to save team',
                color: 'red',
            });
        } finally {
            setIsLoading(false);
        }

    }

    const negativeAction = () => {
        setIsLoading(true);
        openConfirmModal(
            () => {
                let action: Promise<void>;
                if (!!team?.ownerId && team.ownerId === accessor?.id) {
                    console.log('delete team');
                    action = deleteTeam();
                } else {
                    console.log('leave team');
                    action = leaveTeam();
                }
                action.then(() => {
                    notifications.show({
                        title: 'Success',
                        message: `Team ${!!team ? 'deleted' : 'left'} successfully`,
                        color: 'green',
                    });
                }).catch(() => {
                    notifications.show({
                        title: 'Error',
                        message: 'Failed to delete team',
                        color: 'red',
                    });
                }).finally(() => setIsLoading(false));
            },
            {
                title: 'Confirm Request',
                message: team?.ownerId === accessor?.id ? deleteTeamMessage : leaveTeamMessage,
                confirmLabel: team?.ownerId === accessor?.id ? 'Delete' : 'Leave',
                confirmColor: 'red',
                showIcon: true,
            }
        );
    };

    return (<Card withBorder w={'75%'} mx={'auto'}>
        <ConfirmModalComponent />
        {!team && noTeamView()}
        <Group justify="space-between" ta={'center'} mb={'lg'}>
            <Button
                size={'md'}
                color={'red'}
                variant="light"
                disabled={!team}
                leftSection={<IconLogout2 />}
                onClick={negativeAction}
            >{team?.ownerId === accessor?.id ? 'Delete' : 'Leave'} Team</Button>
            <Text size="1.5rem">{!!team?.ownerId ? 'Edit' : 'Create'} Team</Text>
            <Button
                size={'md'}
                leftSection={<IconDeviceFloppy />}
                disabled={!saveEnabled()}
                onClick={submitForm}
            > {!!team?.ownerId ? 'Save' : 'Create'}</Button>
        </Group>
        {!!team && <Group ta={'center'} my={16} wrap='nowrap' justify='space-around'>
            <TextInput
                w={175}
                label="Join Code"
                size={'lg'}
                value={team.joinCode || 'N/A'}
                readOnly
                rightSection={<ActionIcon onClick={() => clipboard.copy(team.joinCode)} size={'xl'}><IconCopy /></ActionIcon>}
            />
            <TestLink testUrl={team.testAccessId} linkText="Team Test Link" labelOnTop stackButtons />
            <Stack>
                <Text size={'md'} component="p" c={'dimmed'}>
                    Team Size
                </Text>
                <Text component='p' size='1.4rem' c='white'>{teamSize}</Text>
            </Stack>

        </Group>}
        {teamForm()}
    </Card>)
}